import { ReactNode, useEffect, useRef, useState } from "react";
import { Logo } from "@/components/ui/logo";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Activity,
  Bell,
  Users,
  ScrollText,
  Database,
  RadioTower,
  Menu,
  LogOut,
  ChevronsUpDown,
  Search,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useActiveRole } from "@/features/auth/hooks/use-active-role";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { SectionHeader } from "@/components/ui/section-header";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { UserRole } from "@/types/api";
import { useOrg } from "@/store/org-context";
import { getAccessibleOrganizations } from "@/features/organizations/api/organizations-api";
import type { Organization } from "@/types/api";
import { useDebounce } from "@/lib/utils";

type NavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
  description: string;
};

const mainNavigation: NavItem[] = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    description: "Here's what's happening with your water meter network.",
  },
  {
    name: "Properties",
    href: "/properties",
    icon: Building2,
    description: "Manage your properties and devices.",
  },
  {
    name: "Devices",
    href: "/devices",
    icon: Activity,
    description: "Monitor device connectivity and system health.",
  },
  {
    name: "Alerts",
    href: "/alerts",
    icon: Bell,
    description: "Monitor and manage alerts from your device network.",
  },
];

// Manager + Admin + Owner — anyone who can act on the org's people.
// Viewers don't need this view (read-only roles aren't assigning members).
const managerNavigation: NavItem[] = [
  {
    name: "Users",
    href: "/users",
    icon: Users,
    description: "Manage your organization's users and their access levels.",
  },
];

// Visible to Admin + Owner (org-level admin)
const orgAdminNavigation: NavItem[] = [
  {
    name: "Audit Logs",
    href: "/audit-logs",
    icon: ScrollText,
    description: "View a history of all actions taken in your organization.",
  },
];

// Visible to Owner only (system admin)
const ownerNavigation: NavItem[] = [
  {
    name: "Organizations",
    href: "/organizations",
    icon: Building2,
    description: "Manage all organizations.",
  },
  {
    name: "Device Database",
    href: "/device-database",
    icon: Database,
    description: "View and manage provisioned meter credentials.",
  },
  {
    name: "Gateways",
    href: "/admin/gateways",
    icon: RadioTower,
    description: "Provision and manage LoRaWAN gateways.",
  },
  {
    name: "SALT Unit Types",
    href: "/admin/salt-unit-types",
    icon: Database,
    description: "Manage the unit-of-measure dropdown for SALT devices.",
  },
];

// Flat list for route description lookup
const allNavigation = [...mainNavigation, ...managerNavigation, ...orgAdminNavigation, ...ownerNavigation];

interface SidebarLayoutProps {
  children: ReactNode;
}

function NavItem({ item, onClick }: { item: NavItem; onClick: () => void }) {
  return (
    <NavLink
      to={item.href}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all",
          isActive
            ? "bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon
            className={cn(
              "w-5 h-5",
              isActive ? "text-primary-600 dark:text-primary-400" : "text-gray-500 dark:text-gray-400"
            )}
          />
          {item.name}
        </>
      )}
    </NavLink>
  );
}

function OrgSwitcher() {
  const { user } = useAuth();
  const { activeOrgId, activeOrgName, setActiveOrg, resetToOwnOrg, patchActiveName } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [hasMultipleOrgs, setHasMultipleOrgs] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load orgs on mount — resolve current org name and determine if switcher should be visible
  useEffect(() => {
    getAccessibleOrganizations({ perPage: 50 })
      .then(r => {
        setOrgs(r.items);
        setHasMultipleOrgs(r.totalCount > 1);
        const match = r.items.find(o => o.id === activeOrgId);
        if (match && match.name !== activeOrgName) {
          patchActiveName(match.name);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh list when search changes while open
  useEffect(() => {
    if (!open || !debouncedSearch) return;
    getAccessibleOrganizations({ search: debouncedSearch, perPage: 20 })
      .then(r => setOrgs(r.items))
      .catch(() => {});
  }, [open, debouncedSearch]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleSelect(org: Organization) {
    if (org.id === user?.organizationId) {
      resetToOwnOrg(org.id, org.name);
    } else {
      setActiveOrg(org.id, org.name);
    }
    setOpen(false);

    // Redirect away from org-scoped detail pages — the record belongs to the previous org
    if (/^\/properties\/.+/.test(location.pathname)) navigate('/properties');
    else if (/^\/devices\/.+/.test(location.pathname)) navigate('/devices');
  }

  const isOwner = user?.role === UserRole.Owner;

  // Owner (System Admin) always sees the switcher to manage cross-org context.
  // Others only see it when they have multiple accessible orgs.
  if (!isOwner && !hasMultipleOrgs) return null;

  return (
    <div ref={containerRef} className="relative px-4 mb-3">
      <button
        onClick={() => {
          if (!open) {
            setSearchInput('');
            getAccessibleOrganizations({ perPage: 50 }).then(r => setOrgs(r.items)).catch(() => {});
          }
          setOpen(v => !v);
        }}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
      >
        <span className="truncate font-medium text-gray-800 dark:text-gray-200">{activeOrgName || 'Select organization'}</span>
        <ChevronsUpDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute left-4 right-4 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                autoFocus
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search organizations..."
                className="w-full pl-7 pr-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {orgs.length === 0 ? (
              <p className="text-xs text-gray-500 px-3 py-2">No results</p>
            ) : orgs.map(org => (
              <button
                key={org.id}
                onClick={() => handleSelect(org)}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                  org.id === activeOrgId ? "font-semibold text-primary-600 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
                )}
              >
                {org.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function SidebarLayout({ children }: SidebarLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { logout, user } = useAuth();
  const activeRole = useActiveRole();
  const { isViewingForeignOrg, activeOrgName } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const isOrgAdmin = activeRole === UserRole.Admin || activeRole === UserRole.Owner;
  const isOwner = activeRole === UserRole.Owner;
  const canManageUsers = activeRole === UserRole.Manager || isOrgAdmin;
  const currentRoute = allNavigation.find((item) =>
    location.pathname.startsWith(item.href)
  );

  function handleLogoutConfirm() {
    logout();
    navigate("/login");
  }

  return (
    <div className="h-dvh overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-20 px-6 border-b border-gray-200 dark:border-gray-700">
            <Logo variant="full" className="text-[2rem]" />
          </div>

          {/* Org Switcher — shown to all users; hides itself when user has only 1 org */}
          <div className="pt-3">
            <OrgSwitcher />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto overscroll-none">
            {mainNavigation.map((item) => (
              <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
            ))}

            {/* Users — Manager + Admin + Owner. Viewer doesn't need it. */}
            {canManageUsers && managerNavigation.map((item) => (
              <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
            ))}

            {/* Admin section — Audit Logs for Admin+Owner, Organizations for Owner only */}
            {isOrgAdmin && (
              <>
                <div className="pt-4 pb-1">
                  <div className="flex items-center gap-2 px-2">
                    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 select-none">
                      Admin
                    </span>
                    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                  </div>
                </div>
                {orgAdminNavigation.map((item) => (
                  <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
                ))}
                {isOwner && ownerNavigation.map((item) => (
                  <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
                ))}
              </>
            )}
          </nav>

          {/* Theme + Logout */}
          <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 space-y-1">
            <div className="flex items-center justify-between px-3 py-1">
              <span className="text-sm text-gray-500 dark:text-gray-400">Theme</span>
              <ThemeToggle />
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-error-600 hover:text-error-700"
              onClick={() => setShowLogoutDialog(true)}
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 h-full flex flex-col">
        {/* Mobile header */}
        <header className="flex-shrink-0 flex items-center justify-between px-4 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Logo variant="mobile" className="h-12 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-gray-900 leading-tight">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 leading-tight">
                {user?.email}
              </p>
            </div>
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold text-sm flex-shrink-0">
              {user?.firstName?.charAt(0).toUpperCase() || "U"}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overscroll-none pb-[env(safe-area-inset-bottom)]">
          {/* Foreign org banner */}
          {isViewingForeignOrg && user?.role === UserRole.Owner && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>Viewing <strong>{activeOrgName}</strong> — not your organization</span>
            </div>
          )}
          {currentRoute && (
            <SectionHeader
              title={currentRoute.name}
              description={currentRoute.description}
            />
          )}
          {children}
        </main>
      </div>

      <ConfirmDialog
        isOpen={showLogoutDialog}
        title="Sign Out"
        description="Are you sure you want to sign out?"
        confirmLabel="Sign Out"
        onConfirm={handleLogoutConfirm}
        onCancel={() => setShowLogoutDialog(false)}
      />
    </div>
  );
}
